import React from 'react';
import { Table, Badge, Button, Spinner } from 'react-bootstrap';
import { ManageTournament, getTournamentLogoUrl } from '../api/tournament/manageTournamentService';
import '../components/DashboardStyles.css';
import { PermissionWrapper } from '../components/PermissionWrapper/PermissionWrapper';

interface ManageTournamentsFixturesTableProps {
  tournaments: ManageTournament[];
  loading: boolean;
  currentPage: number;
  totalPages: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onViewTournament: (tournament: ManageTournament) => void;
  onEditTournament: (tournament: ManageTournament) => void;
  onDeleteTournament: (tournament: ManageTournament) => void;
}

const ManageTournamentsFixturesTable: React.FC<ManageTournamentsFixturesTableProps> = ({
  tournaments,
  loading,
  currentPage,
  totalPages,
  totalCount,
  onPageChange,
  onViewTournament,
  onEditTournament,
  onDeleteTournament
}) => {
  // Check if tournament can be deleted (not ongoing or completed)
  const canDeleteTournament = (tournament: ManageTournament): boolean => {
    const status = (tournament.status || '').toLowerCase();
    return status !== 'ongoing' && status !== 'completed';
  };


  const renderPagination = () => {
    console.log('🔢 Pagination Debug:', { currentPage, totalPages, totalCount });
    if (totalPages <= 1) {
      console.log('❌ Pagination hidden because totalPages <= 1:', totalPages);
      return null;
    }

    return (
      <div className="pagination-section mt-4" style={{ 
        padding: '16px 0',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {/* Page Info */}
        <div className="page-info mb-3">
          <span className="text-muted" style={{ 
            fontSize: '14px', 
            fontWeight: '500',
            color: '#6c757d'
          }}>
            Showing {((currentPage - 1) * 10) + 1} - {Math.min(currentPage * 10, totalCount)} of {totalCount} tournaments
          </span>
        </div>

        {/* Pagination Controls */}
        <div className="pagination-controls d-flex align-items-center">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="me-2"
          >
            Previous
          </Button>
          
          <div className="page-numbers d-flex align-items-center">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const pageNum = i + 1;
              return (
                <Button
                  key={pageNum}
                  variant={currentPage === pageNum ? "primary" : "outline-primary"}
                  size="sm"
                  onClick={() => onPageChange(pageNum)}
                  className="mx-1"
                  style={{ minWidth: '40px' }}
                >
                  {pageNum}
                </Button>
              );
            })}
          </div>
          
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="ms-2"
          >
            Next
          </Button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-2 text-muted">Loading tournaments...</p>
      </div>
    );
  }

  if (tournaments.length === 0) {
    return (
      <div className="text-center py-5">
        <div className="empty-state">
          <div className="empty-state-icon">
            <i className="ph-trophy" style={{ fontSize: '3rem', color: '#6c757d' }}></i>
          </div>
          <div className="empty-state-title">No Tournaments Found</div>
          <div className="empty-state-description">
            There are no tournaments available for managing fixtures.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="manage-tournaments-table-container">
      <Table responsive hover className="mb-0 manage-tournaments-table" style={{ tableLayout: 'fixed' }}>
        <thead>
          <tr>
            <th style={{ width: '10%', textAlign: 'center' }}>Logo</th>
            <th style={{ width: '40%', textAlign: 'left' }}>Tournament Name</th>
            <th style={{ width: '15%', textAlign: 'left' }}>Sports</th>
            <th style={{ width: '15%', textAlign: 'left' }}>Status</th>
            <th style={{ width: '20%', textAlign: 'center' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {tournaments.map((tournament) => {
            const logoUrl = getTournamentLogoUrl(tournament);
            
            // Create a simple SVG placeholder as data URI (works offline, no external requests)
            // This prevents infinite loops when external placeholder services fail
            const placeholderSvg = 'data:image/svg+xml;base64,' + btoa(
              '<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">' +
              '<rect width="40" height="40" fill="#e9ecef" rx="20"/>' +
              '<text x="50%" y="50%" font-family="Arial, sans-serif" font-size="12" fill="#6c757d" text-anchor="middle" dominant-baseline="middle">LOGO</text>' +
              '</svg>'
            );
            
            return (
            <tr key={tournament._id} className="tournament-row">
              <td style={{ width: '10%', textAlign: 'center', verticalAlign: 'middle', padding: '8px' }}>
                <img
                  src={logoUrl || placeholderSvg}
                  alt={tournament.name}
                  style={{ 
                    width: '40px', 
                    height: '40px', 
                    borderRadius: '50%', 
                    objectFit: 'cover',
                    border: '2px solid #e9ecef',
                    display: 'inline-block'
                  }}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    // Prevent infinite loop: only set fallback if current src is not already the placeholder
                    if (!target.src.includes('data:image/svg+xml')) {
                      target.src = placeholderSvg;
                    } else {
                      // If placeholder also fails, hide the image to prevent infinite loop
                      target.style.display = 'none';
                    }
                  }}
                />
              </td>
              <td>
                <div className="tournament-name-container">
                  <div className="tournament-name">
                    {tournament.name}
                  </div>
                  {tournament.shortName && (
                    <div className="tournament-short-name">
                      {tournament.shortName}
                    </div>
                  )}
                </div>
              </td>
              <td style={{ textAlign: 'left' }}>
                {tournament.sports}
              </td>
              <td style={{ textAlign: 'left' }}>
                {(() => {
                  // Determine if tournament is cancelled
                  const isCancelled = tournament.status === 'cancelled' || tournament.cancellationInfo;
                  
                  return (
                    <Badge 
                      bg={tournament.status === 'completed' ? 'success' : 
                          tournament.status === 'ongoing' ? 'primary' : 
                          tournament.status === 'upcoming' ? 'warning' : 
                          isCancelled ? 'danger' : 'secondary'}
                      className="payment-badge"
                      style={{
                        border: '1px solid',
                        fontWeight: '500',
                        padding: '0.375rem 0.75rem',
                        borderRadius: '6px',
                        backgroundColor: tournament.status === 'completed' ? '#E9FCF2' : 
                                       tournament.status === 'ongoing' ? '#E6F1FF' : 
                                       tournament.status === 'upcoming' ? '#FFF5EC' : 
                                       isCancelled ? '#FEE' : '#F8F9FA',
                        color: tournament.status === 'completed' ? '#1B9E5C' : 
                               tournament.status === 'ongoing' ? '#006EFF' : 
                               tournament.status === 'upcoming' ? '#FD9644' : 
                               isCancelled ? '#DC3545' : '#6C757D',
                        borderColor: tournament.status === 'completed' ? '#1B9E5C' : 
                                    tournament.status === 'ongoing' ? '#006EFF' : 
                                    tournament.status === 'upcoming' ? '#FD9644' : 
                                    isCancelled ? '#DC3545' : '#6C757D',
                        textTransform: 'capitalize'
                      }}
                    >
                      {isCancelled ? 'cancelled' : (tournament.status || 'Unknown')}
                    </Badge>
                  );
                })()}
              </td>
              <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                <div className="tournament-actions" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}>
                  <PermissionWrapper
                    anyOfPermissions={[
                      'tournament.admin',
                      'tournament.fixtures.manage',
                      'tournament.tournaments.manage',
                    ]}
                    hideWhenDisabled
                  >
                    <button
                      className="action-btn btn-view"
                      onClick={() => onViewTournament(tournament)}
                      title="View Details"
                    >
                      <i className="ri-eye-line"></i>
                    </button>
                  </PermissionWrapper>
                  <PermissionWrapper
                    anyOfPermissions={[
                      'tournament.admin',
                      'tournament.fixtures.manage',
                    ]}
                    hideWhenDisabled
                  >
                    <button
                      className="action-btn btn-edit"
                      onClick={() => onEditTournament(tournament)}
                      title="Edit Tournament Fixtures"
                    >
                      <i className="ri-pencil-line"></i>
                    </button>
                  </PermissionWrapper>
                  <PermissionWrapper
                    anyOfPermissions={[
                      'tournament.admin',
                      'tournament.fixtures.manage',
                    ]}
                    hideWhenDisabled
                  >
                    <button
                      className="action-btn btn-delete"
                      onClick={() => onDeleteTournament(tournament)}
                      disabled={!canDeleteTournament(tournament)}
                      title={canDeleteTournament(tournament) ? "Delete Tournament" : "Cannot delete ongoing or completed tournaments"}
                      style={{
                        opacity: canDeleteTournament(tournament) ? 1 : 0.5,
                        cursor: canDeleteTournament(tournament) ? 'pointer' : 'not-allowed'
                      }}
                    >
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  </PermissionWrapper>
                </div>
              </td>
            </tr>
            );
          })}
        </tbody>
      </Table>
      
      {renderPagination()}
    </div>
  );
};

export default ManageTournamentsFixturesTable;
